import { compact } from 'lodash';

// Structural shape of a Principal node we read from. Both
// EstatePage_EntityFragment and EntityDetail_EntityFragment select
// `individual { id }` on each principal-bearing field, so we type the input
// loosely against this minimal shape rather than coupling to a specific
// fragment.
type PrincipalLike = { individual?: { id: string } | null } | null | undefined;

// Structural shape — runtime switch on __typename is the source of truth, so all
// principal-bearing fields are optional here rather than discriminated.
type EntityForPrincipals = {
  subtype: {
    __typename?: string;
    owner?: PrincipalLike;
    owners?: PrincipalLike[] | null;
    grantor?: PrincipalLike;
    grantors?: PrincipalLike[] | null;
    donors?: PrincipalLike[] | null;
  };
};

// Returns the household client-profile IDs that own / are principals on this
// entity. Used to seed owner-aware default tax situs via
// getOwnerAwareDefaultTaxSitus. Returns [] for business entities — the
// fragments we read from don't surface their principals.
export function getEntityPrincipalClientIds(
  entity: EntityForPrincipals | null | undefined
): string[] {
  if (!entity) return [];
  const subtype = entity.subtype;
  if (!subtype || !('__typename' in subtype) || !subtype.__typename) return [];

  switch (subtype.__typename) {
    case 'IndividualPersonalAccount':
    case 'RetirementPersonalAccount':
    case 'CustodialPersonalAccount':
    case 'QualifiedTuitionPersonalAccount':
      return subtype.owner?.individual?.id ? [subtype.owner.individual.id] : [];

    case 'JointPersonalAccount':
    case 'InsurancePersonalAccount':
      return compact((subtype.owners ?? []).map((p) => p?.individual?.id));

    case 'GRATTrust':
    case 'SLATTrust':
      return subtype.grantor?.individual?.id
        ? [subtype.grantor.individual.id]
        : [];

    case 'IrrevocableTrust':
    case 'RevocableTrust':
    case 'ILITTrust':
    case 'QPRTTrust':
    case 'CRTTrust':
    case 'CLTTrust':
      return compact((subtype.grantors ?? []).map((p) => p?.individual?.id));

    case 'DonorAdvisedFund':
    case 'PrivateFoundation':
      return compact((subtype.donors ?? []).map((p) => p?.individual?.id));

    default:
      return [];
  }
}
